import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, signal, ViewChild, WritableSignal, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { BreadcrumbComponent } from '@app/lib/components/breadcrumb/breadcrumb.component';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { IBreadcrumb } from '@app/lib/models/breadcrumb.model';
import { DialogService } from '@app/lib/services/dialog.service';
import { StaffCourtAssignment } from '@app/lib/models/staff-court-assignment.model';
import { StaffCourtAssignmentService } from '../services/staff-court-assignment.services';
import { IResponse } from '@app/lib/models/response.model';
import { ICourtUnitType } from '../models/case-assignment.model';
import { CustomTtRoleDtlsDTO } from '../models/court-staff-roles.model';
import { IJudgeLov } from '../models/reports.model';
import { finalize, forkJoin, switchMap } from 'rxjs';

type ExtendedStaffCourtAssignment = StaffCourtAssignment & { editable?: boolean, editedStartDate?: Date | null, editedLoader?: boolean, rowCourtsList?: ICourtUnitType[] };

@Component({
  selector: 'app-staff-court-assignment',
  standalone: true,
  providers: [DatePipe],
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    BreadcrumbComponent,
    MatDatepickerModule,
    ScrollingModule,
    MatProgressBarModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatTabsModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './staff-court-assignment.component.html',
  styleUrl: './staff-court-assignment.component.scss'
})
export class StaffCourtAssignmentComponent implements OnInit {

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    if (sort) {
      this.matSortRef = sort;
      this.dataSource.sort = sort;
    }
  }
  public matSortRef!: MatSort;

  public breadcrumbs: WritableSignal<IBreadcrumb[]> = signal([
    { label: 'Admin' },
    { label: 'Staff Court Assignment' },
  ]);

  public searchForm: FormGroup = new FormGroup({
    division: new FormControl('', [Validators.required]),

    courts: new FormControl(
      { value: '', disabled: true }
    ),

    staffRole: new FormControl(
      { value: '', disabled: true },
      [Validators.required]
    ),

    staffName: new FormControl(
      { value: '', disabled: true }
    )
  });

  public isLoading: boolean = false;

  // Search Form Loading States
  public isDivisionsLoading: boolean = false;
  public isCourtsLoading: boolean = false;
  public isStaffRolesLoading: boolean = false;
  public isStaffNamesLoading: boolean = false;

  // Search Form Dropdown Data

  public staffRolesList: CustomTtRoleDtlsDTO[] = [];
  public staffNamesList: IJudgeLov[] = [];

  public dataSource = new MatTableDataSource<ExtendedStaffCourtAssignment>();
  public displayedColumns: string[] = ['staffRoleName', 'staffId', 'staffName', 'divisionName', 'courtsName', 'startDate', 'endDate', 'actions'];

  // Temporary mock data arrays instead of API data
  public divisionsList: ICourtUnitType[] = [];
  public courtsList: ICourtUnitType[] = [];

  constructor(
    private dialogService: DialogService,
    private cdr: ChangeDetectorRef,
    private service: StaffCourtAssignmentService,
    private datePipe: DatePipe
  ) { }

  ngOnInit(): void {
    this.fetchDivisions();
  }

  ngAfterViewInit(): void {
    if (this.matSortRef) {
      this.dataSource.sort = this.matSortRef;
    }
  }

  // --- Mock API Methods ---
  fetchDivisions() {
    this.service.getDivisions().subscribe({
      next: (res: IResponse<ICourtUnitType[]>) => {
        if (res && Array.isArray(res.data)) {
          this.divisionsList = res.data;

          if (this.divisionsList.length == 1) {
            this.searchForm.get('division')?.patchValue(this.divisionsList[0])
            this.onSearchDivisionChange();
          }
        } else {
          //console.log('No court data found.');
        }

      },
      error: (err) => {
        console.error('Error fetching court data:', err);
      },
    });
  }


  onSearchDivisionChange() {

    const selectedDivision = this.searchForm.value.division;

    // Reset controls
    this.searchForm.get('courts')?.disable();
    this.searchForm.get('courts')?.reset();
    this.searchForm.get('staffRole')?.disable();
    this.searchForm.get('staffRole')?.reset();
    this.searchForm.get('staffName')?.disable();
    this.searchForm.get('staffName')?.reset();

    // Division must exist (but allow 0)
    if (!selectedDivision || selectedDivision.courtUnitTypeId === null || selectedDivision.courtUnitTypeId === undefined) {
      return;
    }

    const divisionId = selectedDivision.courtUnitTypeId;

    /* =========================
       🔹 LOAD ROLES (Always load)
    ========================== */

    this.isStaffRolesLoading = true;

    this.service.getRoles(divisionId).subscribe({
      next: (res: IResponse<CustomTtRoleDtlsDTO[]>) => {

        if (res && Array.isArray(res.data)) {
          this.staffRolesList = res.data.filter(role => {
            const name = role.roleName?.trim().toLowerCase();
            return name === 'judge' || name === 'court registrar';
          });
          if (this.staffRolesList.length == 1) {
            this.searchForm.get('staffRole')?.patchValue(this.staffRolesList[0])
          }
        } else {
          this.staffRolesList = [];
        }

        this.searchForm.get('staffRole')?.[
          this.staffRolesList.length ? 'enable' : 'disable'
        ]();
      },
      error: (err) => {
        console.error('Error fetching roles:', err);
        this.staffRolesList = [];
      },
      complete: () => {
        this.isStaffRolesLoading = false;
      }
    });

    /* =========================
       🔹 LOAD COURTS (Only if divisionId != 0)
    ========================== */

    if (divisionId === 0) {
      return; // only skip courts, NOT roles
    }

    this.isCourtsLoading = true;

    this.service.getCourts(divisionId).subscribe({
      next: (res: IResponse<ICourtUnitType[]>) => {

        if (res && Array.isArray(res.success)) {
          this.courtsList = res.success ?? [];
          if (res.success.length == 1) {
            this.searchForm.get('courts')?.patchValue(res.success[0])
          }
        } else {
          this.courtsList = [];
        }

        this.searchForm.get('courts')?.[
          this.courtsList.length ? 'enable' : 'disable'
        ]();
      },
      error: (err) => {
        console.error('Error fetching courts:', err);
        this.courtsList = [];
      },
      complete: () => {
        this.isCourtsLoading = false;
      }
    });
  }
  onSearchStaffRoleChange() {
    this.isStaffNamesLoading = true;
    this.searchForm.get('staffName')?.disable();
    this.searchForm.get('staffName')?.reset();

    const selectedRole = this.searchForm.value.staffRole;

    if (selectedRole) {
      const queryParams = {
        "roleName": selectedRole.roleName,
        "intRoleId": selectedRole.intRoleId
      };
      this.service.getRoleStaff(queryParams).subscribe({
        next: (res: IResponse<IJudgeLov[]>) => {
          if (res && Array.isArray(res.data)) {
            this.staffNamesList = res.data ?? [];
            if (this.staffNamesList.length !== 0) {
              this.searchForm.get('staffName')?.enable();
              if (this.staffNamesList.length == 1) {
                this.searchForm.get('staffName')?.patchValue(this.staffNamesList[0])
              }
            }
            else {
              this.searchForm.get('staffName')?.disable();
            }
            this.isStaffNamesLoading = false;
          } else {
            this.isStaffNamesLoading = false;
            //console.log('No court data found.');
          }

        },
        error: (err) => {
          this.isStaffNamesLoading = false;
          console.error('Error fetching court data:', err);
        },
      });
    }
  }
  // ------------------------

  public searchHandler() {

    if (!this.searchForm.valid) {
      this.dialogService.openDialog1({
        data: {
          title: "Information",
          message: 'Please select Division/Staff Role',
          icon: 'info',
        },
        disableClose: true,
        hasBackdrop: true,
        backdropClass: 'dialog-backdrop',
      });
      return;
    }

    this.isLoading = true;

    const formData = this.searchForm.getRawValue(); // safer if controls disabled

    const reqBody = {
      divisionComboId: formData.division?.courtUnitTypeId ?? null,
      courtsComboId: formData.courts?.courtUnitTypeId ?? null,
      staffRoleName: formData.staffRole?.roleName ?? null,
      staffRoleId: formData.staffRole?.intRoleId ?? null,
      staffName: formData.staffName?.prtName ?? null,
      staffId: formData.staffName?.prtId ?? null
    };

    this.service.searchStaffData(reqBody).subscribe({
      next: (res: IResponse<StaffCourtAssignment[]>) => {

        if (res && Array.isArray(res.data)) {
          this.dataSource.data = res.data ?? [];
        } else {
          this.dataSource.data = [];
          //console.log('No staff data found.');
        }

        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error fetching staff data:', err);
        this.dataSource.data = [];
      },
      complete: () => {
        this.isLoading = false; // ✅ Always closes loader
      }
    });
  }

  public resetHandler() {
    this.searchForm.reset();
    this.searchForm.get('courts')?.disable();
    this.searchForm.get('staffRole')?.disable();
    this.searchForm.get('staffName')?.disable();
    this.dataSource.data = [];
  }

  public isRecordEditable(row: ExtendedStaffCourtAssignment): boolean {
    if (!row.endDate) return true;
    const end = new Date(row.endDate).setHours(0, 0, 0, 0);
    const today = new Date().setHours(0, 0, 0, 0);
    return end > today;
  }

  public onEditToggle(row: ExtendedStaffCourtAssignment): void {
    row.editable = !row.editable;
    if (row.editable) {
      row.editedStartDate = row.startDate ? new Date(row.startDate) : new Date();
      if (row.divisionId) {
        this.service.getCourts(row.divisionId).subscribe({
          next: (res: IResponse<ICourtUnitType[]>) => {
            row.rowCourtsList = Array.isArray(res.success) ? res.success : [];
            this.cdr.detectChanges();
          },
          error: (err) => {
            console.error('Error fetching courts for edit row:', err);
            row.rowCourtsList = [];
          }
        });
      } else {
        row.rowCourtsList = [];
      }
    }
  }

  public onDivisionChange(row: ExtendedStaffCourtAssignment, selectedDivisionId: number): void {
    row.divisionId = selectedDivisionId;
    row.courtsId = null; // Clear dependent court selection
    row.courtsName = null;

    // Fetch newly dependent courts
    this.service.getCourts(selectedDivisionId).subscribe({
      next: (res: IResponse<ICourtUnitType[]>) => {
        row.rowCourtsList = Array.isArray(res.success) ? res.success : [];
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error fetching courts on division change:', err);
        row.rowCourtsList = [];
      }
    });
  }

  public onSave(row: ExtendedStaffCourtAssignment): void {
    if (!row.divisionId || !row.courtsId || !row.editedStartDate) {
      this.dialogService.openDialog1({
        data: { title: "Validation Error", message: 'Please provide Division, Court, and Begin Date.', icon: 'error' },
        disableClose: true, hasBackdrop: true, backdropClass: 'dialog-backdrop',
      });
      return;
    }

    row.editedLoader = true;

    row.startDate = row.editedStartDate?.getTime() || row.startDate;

    const selectedDiv = this.divisionsList.find(d => d.courtUnitTypeId === row.divisionId);
    if (selectedDiv) row.divisionName = selectedDiv.courtUnitTypeName;

    const selectedCourt = row.rowCourtsList?.find(c => c.courtUnitTypeId === row.courtsId);
    if (selectedCourt) row.courtsName = selectedCourt.courtUnitTypeName;

    const payload = [{
      divisionId: row.divisionId,
      divisionName: row.divisionName,
      courtsId: row.courtsId,
      courtsName: row.courtsName,
      transType: "U",
      staffRoleId: (row as any).staffRoleId,
      staffRoleName: row.staffRoleName,
      staffId: row.staffId,
      staffName: row.staffName,
      startDate: row.startDate ? this.datePipe.transform(row.startDate, 'yyyy-MM-ddTHH:mm:ss') : null,
      validationStartDate: (row as any).validationStartDate ? this.datePipe.transform((row as any).validationStartDate, 'yyyy-MM-ddTHH:mm:ss') : null,
      endDate: row.endDate ? this.datePipe.transform(row.endDate, 'yyyy-MM-ddTHH:mm:ss') : null,
      userActiveUntil: (row as any).userActiveUntil ? this.datePipe.transform((row as any).userActiveUntil, 'yyyy-MM-ddTHH:mm:ss') : null
    }];

    //console.log('Save Payload:', payload);

    this.service.checkStatus(payload).pipe(
      switchMap((res: IResponse<any>) => {
        if (res.success) {
          return this.service.saveData(payload);
        } else {
          throw { info: 'Staff Not available' };
        }
      })
    ).subscribe({
      next: (res: IResponse<any>) => {
        if (res.success) {
          this.dialogService.openDialog1({
            data: { title: 'Success', message: res.message, icon: 'check_circle' },
            disableClose: true, hasBackdrop: true, backdropClass: 'dialog-backdrop',
          });
        } else {
          this.dialogService.openDialog1({
            data: { title: 'Information', message: res.message, icon: 'info' },
            disableClose: true, hasBackdrop: true, backdropClass: 'dialog-backdrop',
          });
        }
      },
      error: (err) => {
        this.dialogService.openDialog1({
          data: { title: 'Information', message: err.info || 'An error occurred', icon: 'info' },
          disableClose: true, hasBackdrop: true, backdropClass: 'dialog-backdrop',
        });
        console.error('Save Error:', err);
      },
      complete: () => {
        row.editable = false;
        row.editedLoader = false;
        this.searchHandler();
      }
    });
  }
}
